// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

const OPENWEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;
const BASE_URL = 'https://api.openweathermap.org/data/2.5';

/** Format hour (0-23) as 12am, 1am, ... 11am, 12pm, 1pm, ... 11pm */
function formatHour12am11pm(hour) {
  const h = hour % 12;
  const ampm = hour < 12 ? 'am' : 'pm';
  return (h === 0 ? 12 : h) + ampm;
}

/** Parse "12am", "1am", "9pm" etc. to hour 0-23, or undefined if invalid */
function parseHourFromLabel(label) {
  if (!label || typeof label !== 'string') return undefined;
  const m = label.toLowerCase().match(/^(\d{1,2})(am|pm)$/);
  if (!m) return undefined;
  let h = parseInt(m[1], 10);
  if (m[2] === 'am') return h === 12 ? 0 : h;
  return h === 12 ? 12 : h + 12;
}

export const getWeatherByCoords = async (lat, lon) => {
  try {
    const response = await fetch(
      `${BASE_URL}/weather?lat=${lat}&lon=${lon}&units=imperial&appid=${OPENWEATHER_API_KEY}`
    );
    
    if (!response.ok) {
      throw new Error('Weather data not available');
    }
    
    const data = await response.json();
    
    return {
      temp: Math.round(data.main.temp),
      feelsLike: Math.round(data.main.feels_like),
      condition: data.weather[0].main,
      description: data.weather[0].description,
      humidity: data.main.humidity,
      windSpeed: Math.round(data.wind.speed),
      windDirection: data.wind.deg,
      pressure: data.main.pressure,
      visibility: data.visibility,
      icon: data.weather[0].icon,
      location: data.name
    };
  } catch (error) {
    console.error('Error fetching weather:', error);
    return null;
  }
};

export const getForecastByCoords = async (lat, lon) => {
  try {
    const response = await fetch(
      `${BASE_URL}/forecast?lat=${lat}&lon=${lon}&units=imperial&appid=${OPENWEATHER_API_KEY}`
    );
    
    if (!response.ok) {
      throw new Error('Forecast data not available');
    }
    
    const data = await response.json();

    // Group forecast by day
    const dailyForecasts = {};

    data.list.forEach(item => {
      const baseDate = new Date(item.dt * 1000);
      const dateKey = baseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      if (!dailyForecasts[dateKey]) {
        dailyForecasts[dateKey] = {
          date: dateKey,
          temps: [],
          conditions: [],
          humidity: [],
          wind: [],
          forecasts: []
        };
      }

      dailyForecasts[dateKey].temps.push(item.main.temp);
      dailyForecasts[dateKey].conditions.push(item.weather[0].main);
      dailyForecasts[dateKey].humidity.push(item.main.humidity);
      dailyForecasts[dateKey].wind.push(item.wind.speed);

      // Expand each 3-hour forecast entry into up to 3 hourly entries
      for (let offset = 0; offset < 3; offset++) {
        const hourDate = new Date(baseDate.getTime() + offset * 60 * 60 * 1000);

        // Stop if we've crossed into the next calendar day
        const hourDateKey = hourDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (hourDateKey !== dateKey) break;

        const hour = hourDate.getHours();
        const hourLabel = formatHour12am11pm(hour);
        dailyForecasts[dateKey].forecasts.push({
          time: hourLabel,
          temp: Math.round(item.main.temp),
          condition: item.weather[0].main,
          icon: item.weather[0].icon,
          description: item.weather[0].description
        });
      }
    });

    // Calculate daily averages and build 5-day forecast with full 24h hourly (12am–11pm).
    // API omits past 3h slots, so we fill missing hours from the next available slot.
    const daily = Object.values(dailyForecasts)
      .slice(0, 5)
      .map(day => {
        const hourlyByHour = new Map();
        day.forecasts.forEach(f => {
          const hour = parseHourFromLabel(f.time);
          if (hour !== undefined && !hourlyByHour.has(hour)) {
            hourlyByHour.set(hour, { temp: f.temp, condition: f.condition, icon: f.icon, description: f.description });
          }
        });
        const fullHourly = [];
        const hoursWithData = [...hourlyByHour.keys()].sort((a, b) => a - b);
        const firstHourWithData = hoursWithData.length > 0 ? hoursWithData[0] : 24;
        let fallback = firstHourWithData < 24
          ? hourlyByHour.get(firstHourWithData)
          : (day.forecasts[0] ? { temp: Math.round(day.temps[0] ?? 0), condition: day.conditions[0], icon: null, description: '' } : null);
        for (let h = 0; h < 24; h++) {
          const label = formatHour12am11pm(h);
          const entry = hourlyByHour.get(h);
          if (entry) {
            fallback = entry;
            fullHourly.push({ time: label, temp: entry.temp, condition: entry.condition, icon: entry.icon, description: entry.description });
          } else if (fallback) {
            fullHourly.push({ time: label, temp: fallback.temp, condition: fallback.condition, icon: fallback.icon, description: fallback.description });
          }
        }
        const hourly = fullHourly.length > 0 ? fullHourly : day.forecasts;
        return {
          date: day.date,
          tempHigh: Math.round(Math.max(...day.temps)),
          tempLow: Math.round(Math.min(...day.temps)),
          condition: day.conditions[0],
          humidity: Math.round(day.humidity.reduce((a, b) => a + b, 0) / day.humidity.length),
          windSpeed: Math.round(day.wind.reduce((a, b) => a + b, 0) / day.wind.length),
          hourly
        };
      });

    return {
      location: data.city.name,
      daily
    };
  } catch (error) {
    console.error('Error fetching forecast:', error);
    return null;
  }
};

export const getWeatherIcon = (condition, isNight = false) => {
  if (condition === 'Clear') {
    return isNight ? '🌙' : '☀️';
  }

  const icons = {
    'Clouds': '☁️',
    'Rain': '🌧️',
    'Drizzle': '🌦️',
    'Thunderstorm': '⛈️',
    'Snow': '❄️',
    'Mist': '🌫️',
    'Fog': '🌫️',
    'Haze': '🌫️'
  };
  
  return icons[condition] || '🌤️';
};